import React, { useState } from "react";
import {
    useGetMyApiStatsQuery,
    useGetAllApiStatsQuery,
    useGetTopEndpointsQuery,
    useGetTopUsersQuery,
} from "../store/api";
import { useSelector } from "react-redux";
import { RootState } from "../store";

type TabType = "my" | "all" | "endpoints" | "users";

const ApiStats: React.FC = () => {
    const [activeTab, setActiveTab] = useState<TabType>("my");
    const [limit, setLimit] = useState(50);
    const [offset, setOffset] = useState(0);
    const [filterUserId, setFilterUserId] = useState<string>("");
    const [filterEndpoint, setFilterEndpoint] = useState<string>("");
    const [showOnlyErrors, setShowOnlyErrors] = useState(false);

    // Фильтры по датам для топа пользователей (по умолчанию последний месяц)
    const [fromDate, setFromDate] = useState<string>(
        new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0]
    );
    const [toDate, setToDate] = useState<string>(
        new Date().toISOString().split("T")[0]
    );

    const currentUser = useSelector((state: RootState) => state.auth.user);
    const isAdmin = currentUser?.is_admin || false;

    // Запросы
    const { data: myStats, isLoading: myLoading } = useGetMyApiStatsQuery(
        { limit, has_errors: showOnlyErrors },
        { skip: activeTab !== "my" }
    );

    const { data: allStats, isLoading: allLoading } = useGetAllApiStatsQuery(
        {
            user_id: filterUserId ? parseInt(filterUserId) : undefined,
            endpoint: filterEndpoint || undefined,
            limit,
            offset,
            has_errors: showOnlyErrors,
        },
        { skip: activeTab !== "all" || !isAdmin }
    );

    const { data: topEndpoints, isLoading: endpointsLoading } =
        useGetTopEndpointsQuery(
            { limit: 20 },
            { skip: activeTab !== "endpoints" || !isAdmin }
        );

    const { data: topUsers, isLoading: usersLoading } = useGetTopUsersQuery(
        { limit: 10, from_date: fromDate, to_date: toDate },
        { skip: activeTab !== "users" || !isAdmin }
    );

    const formatDate = (dateString: string | null) => {
        if (!dateString) return "—";
        return new Date(dateString).toLocaleString("ru-RU");
    };

    const getStatusBadgeColor = (statusCode: number | null) => {
        if (!statusCode) return "bg-gray-500";
        if (statusCode >= 200 && statusCode < 300) return "bg-green-500";
        if (statusCode >= 400 && statusCode < 500) return "bg-yellow-500";
        if (statusCode >= 500) return "bg-red-500";
        return "bg-gray-500";
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                    Статистика API запросов
                </h1>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Мониторинг активности и производительности API
                </p>
            </div>

            {/* Табы */}
            <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex gap-6">
                    <button
                        onClick={() => setActiveTab("my")}
                        className={`py-4 px-1 border-b-2 font-medium text-sm ${
                            activeTab === "my"
                                ? "border-blue-500 text-blue-600 dark:text-blue-400"
                                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                        }`}
                    >
                        Моя статистика
                    </button>

                    {isAdmin && (
                        <>
                            <button
                                onClick={() => setActiveTab("all")}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                    activeTab === "all"
                                        ? "border-blue-500 text-blue-600 dark:text-blue-400"
                                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                }`}
                            >
                                Все пользователи
                            </button>

                            <button
                                onClick={() => setActiveTab("endpoints")}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                    activeTab === "endpoints"
                                        ? "border-blue-500 text-blue-600 dark:text-blue-400"
                                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                }`}
                            >
                                Топ эндпойнтов
                            </button>

                            <button
                                onClick={() => setActiveTab("users")}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                    activeTab === "users"
                                        ? "border-blue-500 text-blue-600 dark:text-blue-400"
                                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                                }`}
                            >
                                Топ пользователей
                            </button>
                        </>
                    )}
                </nav>
            </div>

            {/* Моя статистика */}
            {activeTab === "my" && (
                <div>
                    {/* Фильтр по ошибкам */}
                    <div className="mb-4 bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={showOnlyErrors}
                                onChange={(e) =>
                                    setShowOnlyErrors(e.target.checked)
                                }
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Показать только с ошибками
                            </span>
                        </label>
                    </div>

                    {myLoading ? (
                        <div className="text-center py-12">Загрузка...</div>
                    ) : myStats?.data ? (
                        <>
                            {/* Сводка */}
                            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                        Всего запросов
                                    </div>
                                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                        {myStats.data.summary.total_requests}
                                    </div>
                                </div>

                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                        Успешных
                                    </div>
                                    <div className="text-2xl font-bold text-green-600">
                                        {myStats.data.summary.total_success}
                                    </div>
                                </div>

                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                        Ошибок
                                    </div>
                                    <div className="text-2xl font-bold text-red-600">
                                        {myStats.data.summary.total_errors}
                                    </div>
                                </div>

                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                        % успешности
                                    </div>
                                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                        {myStats.data.summary.success_rate}%
                                    </div>
                                </div>

                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                        Средн. время
                                    </div>
                                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                        {myStats.data.summary.overall_avg_duration_ms.toFixed(
                                            0
                                        )}{" "}
                                        мс
                                    </div>
                                </div>

                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                        Эндпойнтов
                                    </div>
                                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                        {myStats.data.summary.unique_endpoints}
                                    </div>
                                </div>
                            </div>

                            {/* Таблица */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead className="bg-gray-50 dark:bg-gray-900">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Эндпойнт
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Метод
                                                </th>
                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Запросов
                                                </th>
                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Успех / Ошибки
                                                </th>
                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Средн. время
                                                </th>
                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    % успешности
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Последний запрос
                                                </th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Статус
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                            {myStats.data.stats.map(
                                                (stat: any, index: number) => (
                                                    <tr
                                                        key={index}
                                                        className="hover:bg-gray-50 dark:hover:bg-gray-700"
                                                    >
                                                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white font-mono">
                                                            {stat.endpoint}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm">
                                                            <span
                                                                className={`px-2 py-1 rounded text-white text-xs font-semibold ${
                                                                    stat.method ===
                                                                    "GET"
                                                                        ? "bg-blue-500"
                                                                        : stat.method ===
                                                                          "POST"
                                                                        ? "bg-green-500"
                                                                        : stat.method ===
                                                                              "PUT" ||
                                                                          stat.method ===
                                                                              "PATCH"
                                                                        ? "bg-yellow-500"
                                                                        : "bg-red-500"
                                                                }`}
                                                            >
                                                                {stat.method}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-right text-gray-900 dark:text-white">
                                                            {
                                                                stat.requests_count
                                                            }
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-right">
                                                            <span className="text-green-600">
                                                                {
                                                                    stat.success_count
                                                                }
                                                            </span>
                                                            {" / "}
                                                            <span className="text-red-600">
                                                                {
                                                                    stat.error_count
                                                                }
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-right text-gray-900 dark:text-white">
                                                            {stat.avg_duration_ms.toFixed(
                                                                0
                                                            )}{" "}
                                                            мс
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-right">
                                                            <span
                                                                className={
                                                                    stat.success_rate >=
                                                                    95
                                                                        ? "text-green-600 font-semibold"
                                                                        : stat.success_rate >=
                                                                          80
                                                                        ? "text-yellow-600"
                                                                        : "text-red-600"
                                                                }
                                                            >
                                                                {
                                                                    stat.success_rate
                                                                }
                                                                %
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                                                            {formatDate(
                                                                stat.last_request_at
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 text-center">
                                                            {stat.last_status_code && (
                                                                <span
                                                                    className={`px-2 py-1 rounded text-white text-xs font-semibold ${getStatusBadgeColor(
                                                                        stat.last_status_code
                                                                    )}`}
                                                                >
                                                                    {
                                                                        stat.last_status_code
                                                                    }
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            Нет данных
                        </div>
                    )}
                </div>
            )}

            {/* Топ эндпойнтов (админ) */}
            {activeTab === "endpoints" && isAdmin && (
                <div>
                    {endpointsLoading ? (
                        <div className="text-center py-12">Загрузка...</div>
                    ) : topEndpoints?.data ? (
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-900">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Эндпойнт
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Метод
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Всего запросов
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Успех / Ошибки
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Польз.
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Средн. время
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                % ошибок
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {topEndpoints.data.map(
                                            (stat: any, index: number) => (
                                                <tr
                                                    key={index}
                                                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                                                >
                                                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white font-mono">
                                                        {stat.endpoint}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm">
                                                        <span
                                                            className={`px-2 py-1 rounded text-white text-xs font-semibold ${
                                                                stat.method ===
                                                                "GET"
                                                                    ? "bg-blue-500"
                                                                    : stat.method ===
                                                                      "POST"
                                                                    ? "bg-green-500"
                                                                    : stat.method ===
                                                                          "PUT" ||
                                                                      stat.method ===
                                                                          "PATCH"
                                                                    ? "bg-yellow-500"
                                                                    : "bg-red-500"
                                                            }`}
                                                        >
                                                            {stat.method}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-right text-gray-900 dark:text-white font-semibold">
                                                        {stat.total_requests}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-right">
                                                        <span className="text-green-600">
                                                            {stat.total_success}
                                                        </span>
                                                        {" / "}
                                                        <span className="text-red-600">
                                                            {stat.total_errors}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-right text-gray-900 dark:text-white">
                                                        {stat.unique_users}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-right text-gray-900 dark:text-white">
                                                        {stat.avg_duration_ms.toFixed(
                                                            0
                                                        )}{" "}
                                                        мс
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-right">
                                                        <span
                                                            className={
                                                                stat.error_rate >
                                                                10
                                                                    ? "text-red-600 font-semibold"
                                                                    : stat.error_rate >
                                                                      5
                                                                    ? "text-yellow-600"
                                                                    : "text-green-600"
                                                            }
                                                        >
                                                            {stat.error_rate}%
                                                        </span>
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            Нет данных
                        </div>
                    )}
                </div>
            )}

            {/* Топ пользователей (админ) */}
            {activeTab === "users" && isAdmin && (
                <div>
                    {/* Фильтры по датам */}
                    <div className="mb-4 bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Дата начала
                                </label>
                                <input
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) =>
                                        setFromDate(e.target.value)
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Дата окончания
                                </label>
                                <input
                                    type="date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>
                        </div>

                        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                            По умолчанию показаны данные за последний месяц
                        </div>
                    </div>

                    {usersLoading ? (
                        <div className="text-center py-12">Загрузка...</div>
                    ) : topUsers?.data ? (
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-900">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Пользователь
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Запросов
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Эндпойнтов
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Успех / Ошибки
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Средн. время
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                % успешности
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                Последняя активность
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {topUsers.data.map(
                                            (stat: any, index: number) => (
                                                <tr
                                                    key={index}
                                                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                                                >
                                                    <td className="px-6 py-4">
                                                        {stat.user ? (
                                                            <div>
                                                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                                    {
                                                                        stat
                                                                            .user
                                                                            .name
                                                                    }
                                                                </div>
                                                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                    {
                                                                        stat
                                                                            .user
                                                                            .email
                                                                    }
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-400">
                                                                —
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-right text-gray-900 dark:text-white font-semibold">
                                                        {stat.total_requests}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-right text-gray-900 dark:text-white">
                                                        {stat.unique_endpoints}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-right">
                                                        <span className="text-green-600">
                                                            {stat.total_success}
                                                        </span>
                                                        {" / "}
                                                        <span className="text-red-600">
                                                            {stat.total_errors}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-right text-gray-900 dark:text-white">
                                                        {stat.avg_duration_ms.toFixed(
                                                            0
                                                        )}{" "}
                                                        мс
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-right">
                                                        <span
                                                            className={
                                                                stat.success_rate >=
                                                                95
                                                                    ? "text-green-600 font-semibold"
                                                                    : stat.success_rate >=
                                                                      80
                                                                    ? "text-yellow-600"
                                                                    : "text-red-600"
                                                            }
                                                        >
                                                            {stat.success_rate}%
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                                                        {formatDate(
                                                            stat.last_activity
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            Нет данных
                        </div>
                    )}
                </div>
            )}

            {/* Вся статистика (админ) */}
            {activeTab === "all" && isAdmin && (
                <div>
                    {/* Фильтры */}
                    <div className="mb-4 bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    ID пользователя
                                </label>
                                <input
                                    type="number"
                                    value={filterUserId}
                                    onChange={(e) => {
                                        setFilterUserId(e.target.value);
                                        setOffset(0);
                                    }}
                                    placeholder="Введите ID"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Эндпойнт
                                </label>
                                <input
                                    type="text"
                                    value={filterEndpoint}
                                    onChange={(e) => {
                                        setFilterEndpoint(e.target.value);
                                        setOffset(0);
                                    }}
                                    placeholder="Например: safe/info"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Лимит на странице
                                </label>
                                <input
                                    type="number"
                                    value={limit}
                                    onChange={(e) => {
                                        setLimit(
                                            parseInt(e.target.value) || 50
                                        );
                                        setOffset(0);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={showOnlyErrors}
                                    onChange={(e) => {
                                        setShowOnlyErrors(e.target.checked);
                                        setOffset(0);
                                    }}
                                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Показать только с ошибками
                                </span>
                            </label>
                        </div>
                    </div>

                    {allLoading ? (
                        <div className="text-center py-12">Загрузка...</div>
                    ) : allStats?.data ? (
                        <>
                            {/* Общая сводка */}
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                        Пользователей
                                    </div>
                                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                        {allStats.data.summary.unique_users}
                                    </div>
                                </div>

                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                        Всего запросов
                                    </div>
                                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                        {allStats.data.summary.total_requests}
                                    </div>
                                </div>

                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                        Успешных
                                    </div>
                                    <div className="text-2xl font-bold text-green-600">
                                        {allStats.data.summary.total_success}
                                    </div>
                                </div>

                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                        Ошибок
                                    </div>
                                    <div className="text-2xl font-bold text-red-600">
                                        {allStats.data.summary.total_errors}
                                    </div>
                                </div>

                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                        Средн. время
                                    </div>
                                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                        {allStats.data.summary.overall_avg_duration_ms.toFixed(
                                            0
                                        )}{" "}
                                        мс
                                    </div>
                                </div>
                            </div>

                            {/* Таблица (аналогична "Моя статистика", но с полем пользователя) */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead className="bg-gray-50 dark:bg-gray-900">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                    Пользователь
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                    Эндпойнт
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                    Метод
                                                </th>
                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                    Запросов
                                                </th>
                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                                    % успешности
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                            {allStats.data.stats.map(
                                                (stat: any, index: number) => (
                                                    <tr
                                                        key={index}
                                                        className="hover:bg-gray-50 dark:hover:bg-gray-700"
                                                    >
                                                        <td className="px-6 py-4">
                                                            {stat.user ? (
                                                                <div>
                                                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                                        {
                                                                            stat
                                                                                .user
                                                                                .name
                                                                        }
                                                                    </div>
                                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                        ID:{" "}
                                                                        {
                                                                            stat
                                                                                .user
                                                                                .id
                                                                        }
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <span className="text-gray-400">
                                                                    —
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white font-mono">
                                                            {stat.endpoint}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm">
                                                            <span
                                                                className={`px-2 py-1 rounded text-white text-xs font-semibold ${
                                                                    stat.method ===
                                                                    "GET"
                                                                        ? "bg-blue-500"
                                                                        : stat.method ===
                                                                          "POST"
                                                                        ? "bg-green-500"
                                                                        : "bg-yellow-500"
                                                                }`}
                                                            >
                                                                {stat.method}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-right text-gray-900 dark:text-white">
                                                            {
                                                                stat.requests_count
                                                            }
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-right">
                                                            <span
                                                                className={
                                                                    stat.success_rate >=
                                                                    95
                                                                        ? "text-green-600 font-semibold"
                                                                        : stat.success_rate >=
                                                                          80
                                                                        ? "text-yellow-600"
                                                                        : "text-red-600"
                                                                }
                                                            >
                                                                {
                                                                    stat.success_rate
                                                                }
                                                                %
                                                            </span>
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Кнопка "Показать еще" */}
                            {allStats.data.pagination.has_more && (
                                <div className="mt-4 flex justify-center">
                                    <button
                                        onClick={() =>
                                            setOffset(offset + limit)
                                        }
                                        disabled={allLoading}
                                        className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors"
                                    >
                                        {allLoading
                                            ? "Загрузка..."
                                            : "Показать еще"}
                                    </button>
                                </div>
                            )}

                            {/* Информация о пагинации */}
                            <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
                                Показано {allStats.data.stats.length} из{" "}
                                {allStats.data.pagination.total} записей
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            Нет данных
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ApiStats;
